import React, { useState } from "react";
import { Container } from "@material-ui/core";
import { CTextField, HFlex, DialogSuccessNotice, ProgressButton, Text, useErrorContext, CAutocomplete } from "shared";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Send } from "assets";
import { delay } from "utils/misc";
import { FORM_NOTIFICATION_DELAY } from "theme";
import { Country, getCountryCode, isUsMarket } from "utils/market";
import usePayers from "routes/patientSearch/queries/usePayers";
import { useMountState } from "utils/hooks";
import useInvitePatient, { type Vars } from "routes/therapist/queries/useInvitePatient";
import type { CountryData } from "react-phone-input-2";
import { PhoneNumberInput } from "shared/PhoneNumberInput";

interface Props {
  onClose: () => void;
  therapistId?: number;
  defaultCountry?: Country;
}

const InvitePatientForm: React.VFC<Props> = ({ onClose, therapistId, defaultCountry }) => {
  const { t } = useTranslation();
  const { mutateAsync, data, isLoading, isError } = useInvitePatient();
  const country = defaultCountry ?? (isUsMarket ? Country.US : Country.SE);
  const defaultCountryCode = getCountryCode(country).toLocaleLowerCase();
  const { data: payers, isLoading: isLoadingPayers } = usePayers(country);
  const setError = useErrorContext();
  const getMountState = useMountState();
  const [dialCode, setDialCode] = useState("");

  const form = useForm<Vars>({
    defaultValues: { therapist_id: therapistId, email: "", payer_name: "", phone_country_code: defaultCountryCode, phone_number: "" },
    mode: "onChange",
    shouldUnregister: true,
  });
  const { control, setValue, handleSubmit } = form;

  const submit = handleSubmit(async ({ phone_number, phone_country_code, email, payer_name, therapist_id }) => {
    const validPhone = !!(phone_number && phone_number !== dialCode)
    if (!email && !validPhone) {
      setError(t`errors.enter_email_or_phone`);
      return;
    }
    try {
      await mutateAsync({
        therapist_id,
        payer_name,
        ...(email ? { email } : {}),
        ...(validPhone ? { phone_number, phone_country_code } : {}),
      });
      if (!getMountState()) return;
      await delay(FORM_NOTIFICATION_DELAY);
      getMountState() && onClose();
    } catch (err) {
      setError(t`errors.could_not_send_invite`, err);
    }
  });

  if (!isError && data)
    return (
      <DialogSuccessNotice
        heading={t("patients.invite_sent")}
        body={`${t("form.invitation_successfully_sent_to")} ${data.email || data.phone_number}`}
        onClose={onClose}
      />
    );

  return (
    <Container>
      <Text heading bold mb={3} id="invite-patient">
        {t("patients.invite_a_patient")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>

          <Controller
            name="phone_number"
            control={control}
            render={({ value, onChange }) => (
              <PhoneNumberInput
                country={defaultCountryCode}
                value={value}
                onChange={(inputValue, data: CountryData, event) => {
                  event.stopPropagation();
                  setDialCode(data.dialCode);
                  setValue("phone_country_code", data.countryCode.toLocaleUpperCase());
                  onChange(inputValue);
                }}
              />
            )}
          />
          <Controller
            name="phone_country_code"
            control={control}
            render={field => <input type="hidden" {...field} />}
          />

          <CTextField
            name="email"
            label={t("form.email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />


          <CTextField
            asNumber
            required
            name="therapist_id"
            type="number"
            label={t("patients.roles.therapist") + " ID"}
            margin="normal"
            size="small"
            disabled={!!therapistId}
            fullWidth
          />
          <CAutocomplete
            getOptionValue={({ name }) => name}
            getOptionLabel={({ name }) => name ?? "NAME MISSING"}
            loading={isLoadingPayers}
            options={payers ?? []}
            name="payer_name"
            label={t("form.payer")}
            margin="normal"
            size="small"
            fullWidth
          />
          <HFlex justifyContent="center" my={6}>
            <ProgressButton loading={isLoading} endIcon={<Send width={20} />}>
              {t("buttons.send_invitation")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Container>
  );
};

export default InvitePatientForm;
